// 統一時間軸（ADR-003 §2.5.3「合併規則只有一條，寫在共用的 playback 層」；plan/vms-web-v0.md §5.6 PLAY-01 / 02 / 08）。
//
// 規則：一條時間軸、每一刻一個來源。來源依優先順序排好丟進來，前面的有錄到就用前面的，
// 後面的只補前面沒有的時段：
//   - Wi-Fi 相機 / station（SD）：[cloud, sd]           —— 雲端優先，SD 補空檔
//   - NVR（硬碟）：              [nvr, cloud, backup]   —— 硬碟優先；NVR 連不上時索引查不到，自然落到雲端
//   - MyCam 電腦端替它錄的攝影機：[node…, cloud, phone | sd] —— 電腦端 → 雲端 → 手機本機 / SD（mycam-v0 §10.3、vms-web §5.19.3）；
//                                  電腦端一台一個 SourceIndex（nodeSourceIndexes），各自的負責時段 = recorder_assignments 歷史
// 沒有任何來源的時段是缺口，原因分開（PLAY-08；依這個順序判）：
//   offline      設備離線（這段時間設備沒連線）
//   paused       來源自己報「這段沒錄，因為 …」（MyCam pause_reason、電腦端 gaps[].reason；cause = 原因）
//   unknown      來源未知（該負責的來源索引查不到、或覆蓋還沒上報到這裡，例如 NVR / 電腦端現在連不上；cause = 哪個來源為什麼）
//   retention    超出保留期（比雲端保留天數更早）
//   not_recorded 無錄影（每個來源都查得到、都沒有：排程沒錄 / 只錄事件的空檔）
// 「來源不在線 ≠ 沒有錄影」（ADR-003 §2.5）：負責時段內、覆蓋還沒上報的部分標 unknown，不當缺口。
// 時間一律 epoch 秒；「現在」之後不算缺口。

export type RecordingSource = 'cloud' | 'nvr' | 'sd' | 'edge' | 'backup' | 'node' | 'phone';
export type GapReason = 'offline' | 'paused' | 'retention' | 'unknown' | 'not_recorded';

export interface Interval {
  start: number;
  end: number;
}

export interface SourceSpan extends Interval {
  event?: boolean;
}

export interface SourceIndex {
  source: RecordingSource;
  /** 索引查到了（查不到 = 這個來源的狀態未知；spans 會被忽略） */
  ok: boolean;
  spans: SourceSpan[];
  /** 這個來源的保留天數（雲端才有）；比 now − days 更早、又沒有別的來源 → retention */
  retentionDays?: number;
  /** 這個來源負責的時段（例：MyCam 電腦端 = recorder_assignments 的 valid_from..valid_to）；沒給 = 整個視窗。
   * 負責時段外它的 spans 不算，也不會讓洞變成「未知」。 */
  scope?: Interval[];
  /** 索引可信的時段（例：電腦端的覆蓋上報到 known_until 為止）；沒給 = 負責時段全部可信（ok=false 時全部不可信）。
   * 負責時段內、可信時段外、又沒有別的來源 → unknown。 */
  known?: Interval[];
  /** unknown 的原因（例：node_offline、node_pending、phone_offline）；沒給 = 來源名。 */
  unknownCause?: string;
  /** 來源自己報的「這段沒錄，因為 …」；沒有別的來源時標 paused（cause = reason）。 */
  pauses?: (Interval & { reason: string })[];
  /** 同一種來源有好幾個時（例：兩台電腦端）的識別，放進 TimelineSpan / TimelineGap 讓畫面知道是哪一台。 */
  ref?: string;
}

export interface TimelineSpan extends Interval {
  source: RecordingSource;
  event: boolean;
  ref?: string;
}

export interface TimelineGap extends Interval {
  reason: GapReason;
  /** paused：暫停原因；unknown：哪個來源為什麼不知道（SourceIndex.unknownCause） */
  cause?: string;
  source?: RecordingSource;
  ref?: string;
}

export interface UnifiedTimeline {
  spans: TimelineSpan[];
  gaps: TimelineGap[];
  /** 每個來源自己的覆蓋（「檢視全部來源」模式用；已裁到視窗內、合併重疊） */
  coverage: { source: RecordingSource; ok: boolean; ref?: string; spans: TimelineSpan[] }[];
}

export interface MergeOptions {
  from: number;
  to: number;
  /** 預設 Date.now()/1000 */
  now?: number;
  /** 已知的離線區間（例如目前離線：[last_seen_at, now]） */
  offline?: Interval[];
  /** 小於這個秒數的縫隙直接接起來（段與段之間的 GOP 邊界），預設 2 */
  joinGapSec?: number;
}

/** 排序、裁切、合併重疊（相同 event 旗標才合併；event 段蓋過連續段）。 */
export function normalizeSpans(spans: SourceSpan[], from: number, to: number, joinGapSec = 2): SourceSpan[] {
  const clipped = spans
    .map((s) => ({ start: Math.max(s.start, from), end: Math.min(s.end, to), event: !!s.event }))
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start || Number(b.event) - Number(a.event));
  // 先把事件段挖出來，連續段扣掉事件段 → 同一刻只有一種旗標
  const events = mergeRaw(clipped.filter((s) => s.event), joinGapSec);
  const plain = subtract(mergeRaw(clipped.filter((s) => !s.event), joinGapSec), events);
  return [...events.map((s) => ({ ...s, event: true })), ...plain.map((s) => ({ ...s, event: false }))].sort((a, b) => a.start - b.start);
}

function mergeRaw(spans: Interval[], joinGapSec: number): Interval[] {
  const out: Interval[] = [];
  for (const s of [...spans].sort((a, b) => a.start - b.start)) {
    const last = out[out.length - 1];
    if (last && s.start <= last.end + joinGapSec) last.end = Math.max(last.end, s.end);
    else out.push({ start: s.start, end: s.end });
  }
  return out;
}

/** a − b（兩邊都已排序且不重疊）。 */
export function subtract<T extends Interval>(a: T[], b: Interval[]): T[] {
  const out: T[] = [];
  for (const s of a) {
    let pieces: T[] = [{ ...s }];
    for (const cut of b) {
      if (cut.end <= s.start || cut.start >= s.end) continue;
      const next: T[] = [];
      for (const p of pieces) {
        if (cut.end <= p.start || cut.start >= p.end) {
          next.push(p);
          continue;
        }
        if (cut.start > p.start) next.push({ ...p, end: cut.start });
        if (cut.end < p.end) next.push({ ...p, start: cut.end });
      }
      pieces = next;
    }
    out.push(...pieces);
  }
  return out;
}

/** spans ∩ scope（保留 span 的其他欄位）。 */
function clipTo<T extends Interval>(spans: T[], scope: Interval[]): T[] {
  const out: T[] = [];
  for (const s of spans) for (const c of scope) {
    const a = Math.max(s.start, c.start);
    const b = Math.min(s.end, c.end);
    if (b > a) out.push({ ...s, start: a, end: b });
  }
  return out.sort((p, q) => p.start - q.start);
}

function intersect(a: Interval[], b: Interval[]): Interval[] {
  const out: Interval[] = [];
  for (const x of a) for (const y of b) {
    const s = Math.max(x.start, y.start);
    const e = Math.min(x.end, y.end);
    if (e > s) out.push({ start: s, end: e });
  }
  return out.sort((p, q) => p.start - q.start);
}

/** 合併：sources 依優先順序（第一個最優先）。 */
export function mergeTimeline(sources: SourceIndex[], opts: MergeOptions): UnifiedTimeline {
  const join = opts.joinGapSec ?? 2;
  const now = opts.now ?? Date.now() / 1000;
  const { from, to } = opts;
  const coverage = sources.map((src) => {
    let spans: SourceSpan[] = src.ok ? normalizeSpans(src.spans, from, to, join) : [];
    if (src.scope) spans = clipTo(spans, mergeRaw(src.scope, 0));
    return {
      source: src.source,
      ok: src.ok,
      ...(src.ref !== undefined ? { ref: src.ref } : {}),
      spans: spans.map((s) => ({ ...s, source: src.source, event: !!s.event, ...(src.ref !== undefined ? { ref: src.ref } : {}) })),
    };
  });

  let taken: Interval[] = [];
  const spans: TimelineSpan[] = [];
  for (const c of coverage) {
    const fresh = subtract(c.spans, taken);
    spans.push(...fresh);
    taken = mergeRaw([...taken, ...fresh], 0);
  }
  spans.sort((a, b) => a.start - b.start);

  // 缺口：視窗內、現在以前、沒有任何來源的時段
  const horizon = Math.min(to, now);
  let gaps: TimelineGap[] = [];
  if (horizon > from) {
    const holes = subtract([{ start: from, end: horizon }], taken).filter((h) => h.end - h.start > join);
    const offline = mergeRaw(opts.offline ?? [], 0);
    const retentionCut = sources.reduce<number | null>((acc, s) => {
      if (!s.ok || !s.retentionDays) return acc;
      const cut = now - s.retentionDays * 86400;
      return acc === null ? cut : Math.max(acc, cut); // 最長保留的那個來源都沒了才算超出保留期
    }, null);
    // 每個來源「該負責、卻不知道」的時段（負責時段 − 可信時段）與它報的暫停
    const whole = [{ start: from, end: horizon }];
    const unknownOf = sources.map((src) => {
      const scope = src.scope ? mergeRaw(src.scope, 0) : whole;
      const known = src.ok ? (src.known ? intersect(scope, mergeRaw(src.known, 0)) : scope) : [];
      return subtract(scope, known);
    });
    const pausesOf = sources.map((src) => {
      const ps = (src.pauses ?? []).filter((p) => p.end > p.start).sort((a, b) => a.start - b.start);
      return src.scope ? clipTo(ps, mergeRaw(src.scope, 0)) : ps; // 負責時段外的暫停不算這個來源的
    });
    const tag = (i: number) => ({ source: sources[i].source, ...(sources[i].ref !== undefined ? { ref: sources[i].ref } : {}) });
    for (const h of holes) {
      // 依優先：離線 > 暫停 > 來源未知 > 超出保留期 > 無錄影；同一個洞可能被切成幾段
      const off = intersect([h], offline);
      for (const o of off) gaps.push({ ...o, reason: 'offline' });
      let rest = subtract([h], off);
      sources.forEach((_, i) => {
        for (const p of pausesOf[i]) {
          const hit = rest.flatMap((r) => intersect([r], [p]));
          for (const x of hit) gaps.push({ ...x, reason: 'paused', cause: p.reason, ...tag(i) });
          rest = subtract(rest, hit);
        }
      });
      sources.forEach((src, i) => {
        const hit = rest.flatMap((r) => intersect([r], unknownOf[i]));
        for (const x of hit) gaps.push({ ...x, reason: 'unknown', cause: src.unknownCause ?? src.source, ...tag(i) });
        rest = subtract(rest, mergeRaw(hit, 0));
      });
      for (const r of rest) {
        const ret = retentionCut !== null ? intersect([r], [{ start: -Infinity, end: retentionCut }]) : [];
        for (const x of ret) gaps.push({ ...x, reason: 'retention' });
        for (const x of subtract([r], ret)) gaps.push({ ...x, reason: 'not_recorded' });
      }
    }
    gaps = gaps.filter((g) => g.end > g.start).sort((a, b) => a.start - b.start);
  }
  return { spans, gaps, coverage };
}

/** 從 at 起播：落在某段就從那裡，否則下一段開頭；回那一段（呼叫端決定一次載多長）。 */
export function spanAt(tl: UnifiedTimeline, at: number): { span: TimelineSpan; start: number; skipped: TimelineGap[] } | null {
  const hit = tl.spans.find((s) => s.end > at);
  if (!hit) return null;
  const start = Math.max(at, hit.start);
  const skipped = start > at ? tl.gaps.filter((g) => g.end > at && g.start < start) : [];
  return { span: hit, start, skipped };
}

/**
 * 連續播放要換下一段時：回下一段與中間跳過的缺口。
 * 「跨來源不靜默跳過缺口」：skipped 非空時 UI 必須告訴使用者跳過了什麼（PLAY-02 驗收）。
 */
export function nextSpan(tl: UnifiedTimeline, after: number): { span: TimelineSpan; sourceChanged: boolean; skipped: TimelineGap[] } | null {
  const cur = tl.spans.find((s) => s.start <= after - 0.001 && s.end >= after - 0.001);
  const next = tl.spans.find((s) => s.start >= after - 0.001 && s !== cur);
  if (!next) return null;
  return {
    span: next,
    sourceChanged: !!cur && cur.source !== next.source,
    skipped: tl.gaps.filter((g) => g.end > after && g.start < next.start),
  };
}

/** 雲端 clip（µs）→ SourceSpan（秒）。 */
export function cloudClipSpans(clips: { clip_start_ts_us: number; clip_end_ts_us: number; has_event?: boolean }[]): SourceSpan[] {
  return clips.map((c) => ({ start: c.clip_start_ts_us / 1e6, end: c.clip_end_ts_us / 1e6, event: !!c.has_event }));
}

/** 本地（pb.timeline）段 → SourceSpan。types 位元照 nvrd rec_type_bit：1 = normal（排程連續），其他（motion / manual / io / pir /
 * pid / … / sound）都算事件錄影。 */
export function localSegmentSpans(segs: { start: number; end: number; types: number }[]): SourceSpan[] {
  return segs.map((s) => ({ start: s.start, end: s.end, event: (s.types & ~1) !== 0 }));
}

// ---- MyCam 電腦端（vms-web §5.19.3、mycam-v0 §10.3）----

/** recorder_assignments 的一段歷史：這段時間由哪台電腦端錄（to = null → 到現在還是它）。 */
export interface RecorderAssignmentSpan {
  node: string;
  from: number;
  to: number | null;
}

/** 電腦端上報的覆蓋（façade recorder-coverage 的一列，時間已換成 epoch 秒）。 */
export interface NodeCoverageRow {
  node: string;
  from: number;
  to: number;
  gaps: { from: number; to: number; reason?: string }[];
  /** 這台電腦端對這台攝影機最後上報到哪 */
  knownUntil: number;
  online: boolean;
}

/**
 * 電腦端 → SourceIndex（一台一個）：
 *   - 負責時段 = 這台的指派歷史（裁到視窗）；
 *   - 有錄 = 覆蓋列 − gaps；
 *   - 可信 = 負責時段 ∩ [‥, known_until)；之後的部分（電腦離線 / 還沒回報）標 unknown，不當缺口；
 *   - gaps 有 reason 的當暫停（paused）。
 * 回傳依指派的時間排（新的在前，改派重疊 60 秒時新的那台優先）。
 */
export function nodeSourceIndexes(assignments: RecorderAssignmentSpan[], rows: NodeCoverageRow[], opts: { from: number; to: number; now?: number }): SourceIndex[] {
  const now = opts.now ?? Date.now() / 1000;
  const nodes = [...new Set(assignments.map((a) => a.node))];
  const lastFrom = (n: string) => Math.max(...assignments.filter((a) => a.node === n).map((a) => a.from));
  nodes.sort((a, b) => lastFrom(b) - lastFrom(a));
  return nodes.map((node) => {
    const scope = mergeRaw(
      assignments
        .filter((a) => a.node === node)
        .map((a) => ({ start: Math.max(a.from, opts.from), end: Math.min(a.to ?? now, opts.to) }))
        .filter((a) => a.end > a.start),
      0,
    );
    const mine = rows.filter((r) => r.node === node);
    const knownUntil = mine.reduce((m, r) => Math.max(m, r.knownUntil, r.to), -Infinity);
    const spans: SourceSpan[] = mine.flatMap((r) =>
      subtract([{ start: r.from, end: r.to }], mergeRaw(r.gaps.map((g) => ({ start: g.from, end: g.to })), 0)),
    );
    const pauses = mine.flatMap((r) => r.gaps.filter((g) => !!g.reason).map((g) => ({ start: g.from, end: g.to, reason: g.reason as string })));
    const online = mine.length ? mine.some((r) => r.online) : false;
    return {
      source: 'node' as RecordingSource,
      ref: node,
      ok: true,
      spans,
      scope,
      known: knownUntil === -Infinity ? [] : [{ start: -Infinity, end: knownUntil }],
      unknownCause: online ? 'node_pending' : 'node_offline',
      pauses,
    };
  });
}

/** façade `recorder-coverage` 的一列 → NodeCoverageRow。 */
export function nodeCoverageFromApi(r: { node: { device_ref: string; presence?: string }; from: string; to: string; gaps?: { from: string; to: string; reason?: string }[]; known_until: string }): NodeCoverageRow {
  const t = (x: string) => Date.parse(x) / 1000;
  return {
    node: r.node.device_ref,
    from: t(r.from),
    to: t(r.to),
    gaps: (r.gaps ?? []).map((g) => ({ from: t(g.from), to: t(g.to), ...(g.reason ? { reason: g.reason } : {}) })),
    knownUntil: t(r.known_until),
    online: r.node.presence === 'online',
  };
}
